import is from '@sindresorhus/is';
import { useMemo } from 'react';

import type { JobArchitectureGenerationInfo } from '~/shared/data-contract/job-architecture';
import type { VersionInformation } from '~/shared/data-contract/job-profile-versions';

import type { JobItemDetails } from '../api/use-job-item-details';
import { useLatestJobItemDetails } from '../api/use-latest-job-item-details';
import {
    getNewerVersions,
    getPublishedVersionInfo,
    hasReviewedOrPublishedVersion,
} from '../job-profile-section/notification-bar/notification-version-utils';
import { isGenerationInProgress } from './use-is-generating';

// Single vocabulary for "what does this job profile need next?". Every surface
// (notification bar, status badges, chat prompts) derives its rendering from
// this state. 'stale' is produced only by useJobProfileWorkflow because it
// needs the version list; resolveWorkflowState covers everything derivable
// from the item itself.
export type JobProfileWorkflowState =
    | 'archived'
    | 'assign-reviewer'
    | 'generating'
    | 'none'
    | 'publish'
    | 'review'
    | 'stale';

// Structural subset shared by JobLevel, JobRole, JobLevelDetails, and
// JobRoleDetails, so list rows and detail views resolve through the same code.
export type JobProfileWorkflowItem = {
    readonly actions: {
        readonly canAssignReviewer: boolean;
        readonly canPublish?: boolean;
    };
    readonly isArchived?: boolean;
    readonly profileGenerationInfo?: JobArchitectureGenerationInfo | null;
    readonly reviewer?: unknown;
    readonly versionInfo?: Partial<Pick<VersionInformation, 'isPublished' | 'isReviewed' | 'version'>> | null;
};

type ResolveWorkflowStateOptions = {
    // List surfaces (status badges) keep showing the actionable state while a
    // generation runs; detail surfaces replace it with the generating state.
    readonly ignoreGeneration?: boolean;
};

const isGenerating = (item: JobProfileWorkflowItem): boolean =>
    isGenerationInProgress(item.profileGenerationInfo ?? undefined);

export const resolveWorkflowState = (
    item: JobProfileWorkflowItem,
    { ignoreGeneration = false }: ResolveWorkflowStateOptions = {},
): Exclude<JobProfileWorkflowState, 'stale'> => {
    if (item.isArchived === true) {
        return 'archived';
    }

    if (!ignoreGeneration && isGenerating(item)) {
        return 'generating';
    }

    if (is.nullOrUndefined(item.versionInfo) || item.versionInfo.isPublished === true) {
        return 'none';
    }

    if (item.versionInfo.isReviewed === true) {
        return item.actions.canPublish === true ? 'publish' : 'none';
    }

    if (!item.actions.canAssignReviewer) {
        return 'none';
    }

    return is.nullOrUndefined(item.reviewer) ? 'assign-reviewer' : 'review';
};

type UseJobProfileWorkflowParams = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly version?: number;
};

type JobProfileWorkflow = {
    readonly currentVersion: number | undefined;
    readonly isNewVersion: boolean;
    readonly item: JobItemDetails | undefined;
    readonly previousPublishedVersion: number | undefined;
    readonly state: JobProfileWorkflowState;
};

// Reads the item at its pinned or latest version (useLatestJobItemDetails) and adds the stale
// guard (hide workflow affordances once a newer version is reviewed or published).
// While the item is still loading, `item` is undefined and `state` is 'none' —
// consumers render nothing in both the loading and the nothing-to-do case.
export const useJobProfileWorkflow = ({
    architectureId,
    itemId,
    itemType,
    version,
}: UseJobProfileWorkflowParams): JobProfileWorkflow => {
    const {
        details: { data: item },
        versions: { data: versions },
    } = useLatestJobItemDetails({ architectureId, itemId, itemType, version });

    return useMemo(() => {
        const { hasPreviousPublishedVersion, lastPublishedVersion } = getPublishedVersionInfo(versions);
        const isStale = hasReviewedOrPublishedVersion(getNewerVersions(versions, item?.versionInfo?.version));

        const state: JobProfileWorkflowState = is.undefined(item)
            ? 'none'
            : isStale
              ? 'stale'
              : resolveWorkflowState(item);

        return {
            currentVersion: item?.versionInfo?.version,
            isNewVersion: hasPreviousPublishedVersion,
            item,
            previousPublishedVersion: lastPublishedVersion?.version,
            state,
        };
    }, [item, versions]);
};
